import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGames } from "../store/slices/gameSlice";
import { Icon } from "@iconify/react";
import axios from "axios";

// Beautiful Toast Notification Component
const Toast = ({ message, type = "warning", show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  const getIcon = () => {
    switch (type) {
      case "warning":
        return "⚠️";
      case "error":
        return "❌";
      case "success":
        return "✅";
      case "info":
        return "ℹ️";
      default:
        return "⚠️";
    }
  };

  const getColors = () => {
    switch (type) {
      case "warning":
        return { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E" };
      case "error":
        return { bg: "#FEE2E2", border: "#EF4444", text: "#991B1B" };
      case "success":
        return { bg: "#D1FAE5", border: "#10B981", text: "#065F46" };
      case "info":
        return { bg: "#DBEAFE", border: "#3B82F6", text: "#1E40AF" };
      default:
        return { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E" };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: "12px",
        padding: "16px 20px",
        maxWidth: "400px",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        zIndex: 10000,
        animation: "slideIn 0.3s ease-out",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <span style={{ fontSize: "20px" }}>{getIcon()}</span>
      <span style={{ color: colors.text, fontWeight: "500", flex: 1 }}>
        {message}
      </span>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          fontSize: "24px",
          cursor: "pointer",
          color: colors.text,
          padding: "4px",
          marginLeft: "8px",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.1)";
          e.currentTarget.style.transform = "scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        ×
      </button>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

// Use API URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
let sportbookGamesFetched = false;

const remoteProviderPrefixes = [
  "bs",
  "ng",
  "aws",
  "swg",
  "km",
  "fc",
  "mg",
  "gamatron",
  "ep",
  "pp",
  "netent",
  "joker",
  "bpg",
  "bng",
  "hab",
  "kgl",
  "rlx",
  "ygg",
  "red",
  "qs",
  "ids",
  "tk",
  "ds",
  "nlc",
  "ga",
  "png",
  "pug",
  "fng",
  "nge",
  "1x2",
  "hak",
  "waz",
  "elk",
  "prs",
];

const Sportbook = () => {
  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  const dispatch = useDispatch();
  const { gameList } = useSelector((state) => state.games);

  const { singleUsers } = useSelector((state) => state.user);
  const currentUser = singleUsers && singleUsers.length ? singleUsers[0] : null;
  const providerAllowanceRaw =
    currentUser?.gameProviderAvailable || "ALLPROVIDER";
  const providerAllowanceList =
    providerAllowanceRaw === "ALLPROVIDER"
      ? null
      : providerAllowanceRaw.split(",").map((s) => s.trim().toLowerCase());
  const typeAllowanceList = currentUser?.gameTypeAvailable
    ? currentUser.gameTypeAvailable
        .split(",")
        .map((s) => s.trim().toLowerCase())
    : ["allgametype"];
  const isTypeAllowed =
    typeAllowanceList.includes("allgametype") ||
    typeAllowanceList.includes("sportbook") ||
    typeAllowanceList.includes("sport");

  const isProviderAllowed = (prov) => {
    if (!currentUser) return true;
    if (providerAllowanceList === null) return true;
    const id = (
      prov.productId ||
      prov.game ||
      prov.productName ||
      prov.name ||
      ""
    ).toLowerCase();
    return (
      providerAllowanceList.includes(id) ||
      providerAllowanceList.includes(
        (prov.productName || prov.name || "").toLowerCase(),
      )
    );
  };

  const loadProviderGamesWithCheck = (provider) => {
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }
    if (!isTypeAllowed) {
      showToast(
        "ท่านไม่สามารถเล่นประเภทเกมส์กีฬาได้ เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }
    if (isProviderAllowed(provider)) {
      loadProviderGames(provider);
    } else {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
    }
  };

  // eslint-disable-next-line no-unused-vars
  const [loading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error] = useState(null);

  // Manage selected provider and provider games (like LiveCasino)
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerGames, setProviderGames] = useState([]);
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const [gamesError, setGamesError] = useState(null);
  // Loading while starting game
  const [gameStartLoading, setGameStartLoading] = useState(false);

  useEffect(() => {
    if (sportbookGamesFetched) return;
    sportbookGamesFetched = true;
    dispatch(fetchGames());
  }, [dispatch]);

  // Load games for a provider from API (GET with providerId in path)
  const loadProviderGames = async (provider) => {
    const providerCode = (
      provider?.game ||
      provider?.productId ||
      ""
    ).toLowerCase();
    const providerName = (
      provider?.name ||
      provider?.productName ||
      ""
    ).toLowerCase();
    const shouldUseRemote =
      remoteProviderPrefixes.includes(providerCode) ||
      remoteProviderPrefixes.includes(providerName);

    if (!shouldUseRemote) {
      setSelectedProvider(null);
      setIsLoadingGames(false);
      setGamesError(null);
      setProviderGames([]);
      return getGameLink(provider);
    }

    setSelectedProvider(provider);
    setIsLoadingGames(true);
    setGamesError(null);

    try {
      const res = await axios({
        method: "GET",
        url: `https://space.mil-168.com/games/${providerCode}.json`,
        responseType: "json",
        maxBodyLength: Infinity,
      });
      const payload = res?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      if (Array.isArray(list)) {
        setProviderGames(list);
        setIsLoadingGames(false);
        return;
      }
    } catch (err) {
      console.warn(
        "Failed to load games from list source, fallback to API",
        err,
      );
    }

    const params = {
      providerPrefix: providerCode,
      categories: "",
      page: 1,
      perPage: 40,
    };

    try {
      const res = await axios({
        method: "GET",
        url: `${API_BASE_URL}/gamelogin/getgamelist`,
        params,
        maxBodyLength: Infinity,
      });
      const list = res?.data?.gamesList
        ? res.data.gamesList
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.games)
            ? res.data.games
            : [];
      setProviderGames(list || []);
    } catch (error) {
      console.error(error);
      setGamesError("ไม่สามารถโหลดรายการเกมได้");
      setProviderGames([]);
    } finally {
      setIsLoadingGames(false);
    }
  };

  const backToProviders = () => {
    setSelectedProvider(null);
    setProviderGames([]);
    setGamesError(null);
  };

  // Start game in a new window
  const getGameLink = async (request) => {
    setGameStartLoading(true);

    let userId = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        userId = parsed?.id || parsed?.user?.id || parsed?.UserID || null;
      }
    } catch (_) {}

    const data = { id: userId };

    if (selectedProvider) {
      data.provider = selectedProvider?.productId || selectedProvider?.game;
      data.gamecode = request?.code || request?.gameCode || request?.gamecode;
    } else {
      data.provider = request?.productId || request?.game;
      data.gamecode = request?.productId || request?.game;
    }

    const gameWindow = window.open("", "_blank");

    try {
      const res = await axios({
        method: "POST",
        url: `${API_BASE_URL}/gamelogin/gamestart`,
        data: { data },
        maxBodyLength: Infinity,
      });
      if (res.data) {
        if (gameWindow) {
          gameWindow.location.href = res.data;
        } else {
          window.location.href = res.data;
        }
      }
    } catch (error) {
      console.error(error);
      if (gameWindow) gameWindow.close();
      alert("เกิดข้อผิดพลาดในการเข้าสู่เกมส์");
    } finally {
      setGameStartLoading(false);
    }
  };

  return (
    <div
      style={{
        padding: "16px 0 40px",
        minHeight: "100vh",
        background: "transparent",
      }}
    >
      <div
        style={{
          margin: "0 auto",
          width: "min(980px, calc(100% - 28px))",
          padding: "20px 14px",
          border: "1px solid var(--primaryColor)",
          borderRadius: "22px",
          background:
            "linear-gradient(165deg, color-mix(in srgb, var(--primaryColor) 18%, rgba(10,10,14,0.94)) 0%, color-mix(in srgb, var(--primaryColor) 10%, rgba(10,10,14,0.9)) 48%, rgba(10,10,14,0.94) 100%)",
          backdropFilter: "blur(16px) saturate(120%)",
          boxShadow:
            "0 16px 40px rgba(0,0,0,0.55), 0 0 48px color-mix(in srgb, var(--primaryColor) 22%, transparent)",
        }}
      >
        <div className="page-container">
          <div
            className="header-section"
            style={{
              position: "relative",
              overflow: "hidden",
              marginBottom: "10px",
            }}
          >
            {/* Animated ambient glow */}
            <div
              style={{
                position: "absolute",
                top: "-50%",
                left: "50%",
                transform: "translateX(-50%)",
                width: 280,
                height: 160,
                borderRadius: "50%",
                background: "var(--primaryColor)",
                filter: "blur(72px)",
                opacity: 0.16,
                pointerEvents: "none",
                animation: "sbHeaderPulse 4s ease-in-out infinite",
              }}
            />

            {/* Main row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                position: "relative",
                zIndex: 1,
                padding: "6px 4px",
              }}
            >
              {/* Icon badge */}
              <div
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: 14,
                  flexShrink: 0,
                  background:
                    "linear-gradient(145deg, var(--primaryColor), color-mix(in srgb, var(--primaryColor) 60%, #000))",
                  boxShadow:
                    "0 6px 20px color-mix(in srgb, var(--primaryColor) 40%, transparent)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Icon
                  icon="fluent-color:sport-24"
                  width={30}
                  height={30}
                  style={{ position: "relative", zIndex: 1 }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: 14,
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 55%)",
                    pointerEvents: "none",
                  }}
                />
              </div>

              {/* Title + subtitle */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1
                  className="page-title"
                  style={{
                    margin: 0,
                    fontSize: 22,
                    fontWeight: 900,
                    color: "#fff",
                    letterSpacing: "0.01em",
                    lineHeight: 1.25,
                    textShadow:
                      "0 4px 18px color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                  }}
                >
                  {selectedProvider ? "เกมจากผู้ให้บริการ" : "กีฬา"}
                </h1>
              </div>

              {/* Right side: back button or count chip */}
              {selectedProvider ? (
                <button
                  className="back-button"
                  onClick={backToProviders}
                  style={{
                    background:
                      "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 40%, rgba(255,255,255,0.1)) 0%, color-mix(in srgb, var(--primaryColor) 26%, rgba(255,255,255,0.04)) 100%)",
                    border:
                      "1px solid color-mix(in srgb, var(--primaryColor) 65%, transparent)",
                    color: "#fff",
                    borderRadius: "25px",
                    padding: "8px 16px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontSize: "14px",
                    minHeight: "40px",
                    boxShadow:
                      "0 8px 20px color-mix(in srgb, var(--primaryColor) 25%, transparent)",
                    flexShrink: 0,
                  }}
                >
                  กลับไปหน้าผู้ให้บริการ
                </button>
              ) : (
                <div
                  style={{
                    padding: "5px 12px",
                    borderRadius: 999,
                    flexShrink: 0,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <Icon
                    icon="solar:gamepad-bold-duotone"
                    width={15}
                    height={15}
                    style={{ opacity: 0.7 }}
                  />
                  {(gameList?.sportbook || []).length} ค่าย
                </div>
              )}
            </div>

            {/* Bottom divider */}
            <div
              style={{
                marginTop: 12,
                height: 1,
                background:
                  "linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--primaryColor) 45%, transparent) 50%, transparent 100%)",
              }}
            />

            <style>{`
              @keyframes sbHeaderPulse {
                0%, 100% { opacity: 0.14; transform: translateX(-50%) scale(1); }
                50% { opacity: 0.22; transform: translateX(-50%) scale(1.15); }
              }
            `}</style>
          </div>

          {/* Content */}
          {!selectedProvider && (
            <>
              {/* Alert for Restricted Game Categories */}
              {!isTypeAllowed && (
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                      borderRadius: "16px",
                      padding: "16px 24px",
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
                      border: "2px solid #EF4444",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "50%",
                        padding: "8px",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ fontSize: "24px" }}>🚫</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h2
                        style={{
                          color: "white",
                          margin: 0,
                          fontSize: "18px",
                          fontWeight: "600",
                        }}
                      >
                        หมวดหมู่เกมส์ที่ไม่สามารถเล่นได้
                      </h2>
                      <p
                        style={{
                          color: "rgba(255, 255, 255, 0.9)",
                          margin: 0,
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      >
                        เนื่องจากท่านติดเงื่อนไข โปร, เทิร์น หรือกฎของเว็บไซต์
                      </p>
                    </div>
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "20px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        color: "white",
                        fontWeight: "500",
                      }}
                    >
                      ไม่อนุญาต
                    </div>
                  </div>
                </div>
              )}

              {/* Playable providers */}
              {isTypeAllowed
                ? (() => {
                    const providers = Array.isArray(gameList?.sportbook)
                      ? gameList.sportbook
                      : [];
                    const playable = providers.filter(isProviderAllowed);

                    // Don't show playable section if user has ALLPROVIDER and allgametype
                    const hasAllAccess =
                      providerAllowanceRaw === "ALLPROVIDER" &&
                      typeAllowanceList.includes("allgametype");

                    if (playable.length > 0 && !hasAllAccess) {
                      return (
                        <div style={{ marginBottom: 32 }}>
                          {/* Enhanced Header for Playable Section */}
                          <div
                            className="playable-section"
                            style={{
                              background:
                                "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 90%, transparent) 0%, color-mix(in srgb, var(--primaryColor) 80%, transparent) 100%)",
                              borderRadius: "16px",
                              padding: "16px 24px",
                              marginBottom: 16,
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              boxShadow:
                                "0 8px 25px color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                              border:
                                "2px solid color-mix(in srgb, var(--primaryColor) 60%, transparent)",
                            }}
                          >
                            <div
                              className="icon-container"
                              style={{
                                background: "rgba(255, 255, 255, 0.2)",
                                borderRadius: "50%",
                                padding: "8px",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span style={{ fontSize: "24px" }}>🎯</span>
                            </div>
                            <div
                              className="content"
                              style={{ flex: 1, textAlign: "center" }}
                            >
                              <h2
                                style={{
                                  color: "white",
                                  margin: 0,
                                  fontSize: "18px",
                                  fontWeight: "600",
                                }}
                              >
                                เกมส์ที่สามารถเล่นได้
                              </h2>
                              <p
                                style={{
                                  color: "rgba(255, 255, 255, 0.9)",
                                  margin: 0,
                                  fontSize: "14px",
                                  marginTop: "4px",
                                }}
                              >
                                คลิกเพื่อเข้าเล่นได้เลย
                              </p>
                            </div>
                            <div
                              style={{
                                marginLeft: "auto",
                                background: "rgba(0, 0, 0, 0.2)",
                                borderRadius: "20px",
                                padding: "6px 12px",
                                fontSize: "12px",
                                color: "white",
                                fontWeight: "500",
                              }}
                            >
                              {playable.length} เกมส์
                            </div>
                          </div>

                          {/* Enhanced Grid for Playable Games */}
                          <div className="responsive-grid">
                            {playable.map((game) => {
                              const img = `https://space.mil-168.com/menu-image/${game?.game}.png`;
                              const src = img.startsWith("http")
                                ? img
                                : `https://space.mil-168.com/${(
                                    img || ""
                                  ).replace(/\.[^.]+$/, ".png")}`;
                              return (
                                <div
                                  key={game.productId || game.game}
                                  style={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "16px",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    border:
                                      "3px solid color-mix(in srgb, var(--primaryColor) 50%, transparent)",
                                    position: "relative",
                                    overflow: "hidden",
                                    aspectRatio: "16/9",
                                    boxShadow:
                                      "0 4px 15px color-mix(in srgb, var(--primaryColor) 20%, transparent)",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1.05) translateY(-4px)";
                                    e.currentTarget.style.boxShadow =
                                      "0 12px 30px color-mix(in srgb, var(--primaryColor) 40%, transparent)";
                                    e.currentTarget.style.borderColor =
                                      "var(--primaryColor)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1) translateY(0)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 15px color-mix(in srgb, var(--primaryColor) 20%, transparent)";
                                    e.currentTarget.style.borderColor =
                                      "color-mix(in srgb, var(--primaryColor) 50%, transparent)";
                                  }}
                                  onClick={() =>
                                    loadProviderGamesWithCheck(game)
                                  }
                                >
                                  {/* Success Badge */}
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "8px",
                                      right: "8px",
                                      background:
                                        "linear-gradient(135deg, var(--primaryColor), color-mix(in srgb, var(--primaryColor) 80%, #000))",
                                      borderRadius: "50%",
                                      width: "32px",
                                      height: "32px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      boxShadow:
                                        "0 2px 8px color-mix(in srgb, var(--primaryColor) 40%, transparent)",
                                      zIndex: 2,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        color: "white",
                                      }}
                                    >
                                      ✓
                                    </span>
                                  </div>

                                  <img
                                    src={src}
                                    alt={game.productName || game.name}
                                    style={{
                                      borderRadius: "inherit",
                                      position: "absolute",
                                      inset: 0,
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      display: "block",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "0",
                                      left: "0",
                                      right: "0",
                                      background:
                                        "linear-gradient(to top, color-mix(in srgb, var(--primaryColor) 90%, transparent), color-mix(in srgb, var(--primaryColor) 70%, transparent))",
                                      color: "white",
                                      padding: "12px",
                                      borderRadius: "0 0 12px 12px",
                                      zIndex: 2,
                                    }}
                                  >
                                    <h3
                                      style={{
                                        fontSize: "14px",
                                        margin: "0",
                                        fontWeight: "600",
                                        textShadow:
                                          "0 1px 2px rgba(0, 0, 0, 0.3)",
                                      }}
                                    >
                                      {game.productName || game.name}
                                    </h3>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }

                    // Show locked only when there are no playable providers AND the user does not have all access AND user is logged in
                    if (playable.length === 0 && !hasAllAccess && currentUser) {
                      return (
                        <div style={{ marginBottom: 24, marginTop: 12 }}>
                          <div
                            className="restricted-section"
                            style={{
                              background:
                                "linear-gradient(135deg, #6B7280 0%, #4B5563 100%)",
                              borderRadius: "12px",
                              padding: "16px 20px",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              boxShadow: "0 6px 20px rgba(107, 114, 128, 0.25)",
                              border: "1px solid #6B7280",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            {/* Animated background pattern */}
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background:
                                  "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.03) 8px, rgba(255,255,255,0.03) 16px)",
                                animation: "slidePattern 4s linear infinite",
                              }}
                            />

                            {/* Icon container */}
                            <div
                              className="icon-container"
                              style={{
                                background: "rgba(255, 255, 255, 0.12)",
                                borderRadius: "50%",
                                padding: "8px",
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",

                                zIndex: 2,
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                              }}
                            >
                              <span style={{ fontSize: "18px" }}>🔒</span>
                            </div>

                            {/* Content */}
                            <div
                              className="content"
                              style={{
                                flex: 1,
                                position: "relative",
                                zIndex: 2,
                              }}
                            >
                              <h3
                                style={{
                                  color: "white",
                                  margin: 0,
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  marginBottom: "4px",
                                  textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                                }}
                              >
                                ไม่สามารถเข้าถึงเกมส์ได้
                              </h3>
                              <p
                                style={{
                                  color: "rgba(255, 255, 255, 0.85)",
                                  margin: 0,
                                  fontSize: "13px",
                                  lineHeight: "1.4",
                                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                }}
                              >
                                ท่านไม่สามารถเล่นเกมส์ในหมวดหมู่นี้ได้
                                เนื่องจากติดเงื่อนไขการเล่น
                              </p>
                            </div>

                            {/* Status badge */}
                            <div
                              style={{
                                background: "rgba(255, 255, 255, 0.15)",
                                borderRadius: "16px",
                                padding: "4px 10px",
                                fontSize: "11px",
                                color: "white",
                                fontWeight: "500",
                                position: "relative",
                                zIndex: 2,
                                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                              }}
                            >
                              จำกัด
                            </div>

                            <style>
                              {`
                            @keyframes slidePattern {
                              0% {
                                transform: translateX(-32px);
                              }
                              100% {
                                transform: translateX(0px);
                              }
                            }
                          `}
                            </style>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })()
                : null}

              {/* All providers */}
              <div style={{ marginTop: 16 }}>
                <div className="responsive-grid">
                  {gameList &&
                    gameList.sportbook &&
                    gameList.sportbook.length > 0 &&
                    gameList.sportbook.map((game) => {
                      const img = `https://space.mil-168.com/menu-image/${game?.game}.png`;
                      const src = img.startsWith("http")
                        ? img
                        : `https://space.mil-168.com/${(img || "").replace(
                            /\.[^.]+$/,
                            ".png",
                          )}`;
                      return (
                        <div
                          key={game.productId || game.game}
                          style={{
                            backgroundColor: "#ffffff",
                            borderRadius: "12px",
                            textAlign: "center",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            border:
                              "2px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                            position: "relative",
                            overflow: "hidden",
                            aspectRatio: "16/9",
                            boxShadow:
                              "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 20px color-mix(in srgb, var(--primaryColor) 35%, transparent)";
                            e.currentTarget.style.borderColor =
                              "var(--primaryColor)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)";
                            e.currentTarget.style.borderColor =
                              "color-mix(in srgb, var(--primaryColor) 30%, transparent)";
                          }}
                          onClick={() => loadProviderGamesWithCheck(game)}
                        >
                          <img
                            src={src}
                            alt={game.productName || game.name}
                            style={{
                              borderRadius: "inherit",
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <h3
                            style={{
                              position: "absolute",
                              bottom: "0",
                              left: "0",
                              right: "0",
                              color: "white",
                              fontSize: "14px",
                              margin: "0",
                              padding: "4px 12px",
                              fontWeight: "600",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "0 0 8px 8px",
                              zIndex: 2,
                            }}
                          >
                            {game.productName || game.name}
                          </h3>
                        </div>
                      );
                    })}
                </div>
              </div>
            </>
          )}

          {selectedProvider && (
            <div>
              <div style={{ color: "#f5f5f5", marginBottom: 12 }}>
                ผู้ให้บริการ:{" "}
                {selectedProvider?.productName || selectedProvider?.name || "-"}
              </div>
              {isLoadingGames && (
                <div style={{ color: "#aaa" }}>กำลังโหลดรายการเกม...</div>
              )}
              {gamesError && (
                <div style={{ color: "#ef4444" }}>{gamesError}</div>
              )}
              {!isLoadingGames && !gamesError && (
                <div className="responsive-grid">
                  {providerGames && providerGames.length > 0 ? (
                    providerGames.map((g) => (
                      <div
                        key={g.gameId || g.id || `${g.providerId}-${g.code}`}
                        style={{
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          border:
                            "2px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                          position: "relative",
                          overflow: "hidden",
                          aspectRatio: "16/9",
                          boxShadow:
                            "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow =
                            "0 8px 20px color-mix(in srgb, var(--primaryColor) 35%, transparent)";
                          e.currentTarget.style.borderColor =
                            "var(--primaryColor)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)";
                          e.currentTarget.style.borderColor =
                            "color-mix(in srgb, var(--primaryColor) 30%, transparent)";
                        }}
                        onClick={() => getGameLink(g)}
                      >
                        <img
                          src={
                            g.image ||
                            (g.img && `${g.img}`) ||
                            (g.logo && `https://space.mil-168.com/${g.logo}`) ||
                            `https://space.mil-168.com/${g.image}`
                          }
                          alt={g.gameName || g.name || g.title || "game"}
                          style={{
                            borderRadius: "inherit",
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <h3
                          style={{
                            position: "absolute",
                            bottom: "0",
                            left: "0",
                            right: "0",
                            color: "white",
                            fontSize: "14px",
                            margin: "0",
                            padding: "4px 12px",
                            fontWeight: "600",
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            borderRadius: "0 0 8px 8px",
                            zIndex: 2,
                          }}
                        >
                          {g.gameName || g.name || g.title || "Game"}
                        </h3>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: "#aaa" }}>
                      ไม่พบเกมสำหรับผู้ให้บริการนี้
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Overlay while starting game */}
          {gameStartLoading && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.4)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
            >
              กำลังเริ่มเกม...
            </div>
          )}

          {/* Toast Notification */}
          <Toast
            message={toast.message}
            type={toast.type}
            show={toast.show}
            onClose={hideToast}
          />

          <style jsx>{`
            .page-container {
              padding: 24px;
            }

            @media (min-width: 768px) {
              .page-container {
                padding: 32px;
              }
            }

            .responsive-grid {
              display: grid;
              grid-template-columns: repeat(1, 1fr);
              gap: 16px;
              margin-top: 20px;
            }

            @media (min-width: 768px) {
              .responsive-grid {
                grid-template-columns: repeat(2, 1fr);
              }
            }

            @media (max-width: 768px) {
              .header-section {
                flex-direction: column !important;
                align-items: flex-start !important;
                gap: 16px !important;
              }

              .page-title {
                font-size: 24px !important;
                width: 100% !important;
                justify-content: center !important;
              }

              .back-button {
                width: 100% !important;
                text-align: center !important;
                padding: 12px 16px !important;
              }

              .playable-section,
              .restricted-section {
                padding: 12px 16px !important;
                flex-direction: column !important;
                text-align: center !important;
                gap: 8px !important;
              }

              .playable-section .icon-container,
              .restricted-section .icon-container {
                width: 32px !important;
                height: 32px !important;
                padding: 6px !important;
              }

              .playable-section .content h2,
              .restricted-section .content h3 {
                font-size: 16px !important;
              }

              .playable-section .content p,
              .restricted-section .content p {
                font-size: 12px !important;
              }
            }
          `}</style>
        </div>
      </div>
    </div>
  );
};

export default Sportbook;
